// Create + list groups and communities. The founder-as-admin invariant is
// enforced server-side; this page just exposes the CRUD.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { GroupTile } from '../components/GroupTile';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import type { Community, Group, GroupType, Me, PinnedCommunity, Tag } from '../types/api';

export function Manage() {
    const api = useApi();
    const { authed, login } = useAuth();
    const [me,           setMe]           = useState<Me | null>(null);
    const [groups,       setGroups]       = useState<Group[]>([]);
    const [communities,  setCommunities]  = useState<Community[]>([]);
    const [locationTags, setLocationTags] = useState<Tag[]>([]);
    const [pinned,       setPinned]       = useState<PinnedCommunity[]>([]);
    const [showGroupForm,     setShowGroupForm]     = useState(false);
    const [showCommunityForm, setShowCommunityForm] = useState(false);

    const load = useCallback(async () => {
        if (!authed) return;
        try {
            const [m, g, c, t, p] = await Promise.all([
                api.get<Me>('/api/me'),
                api.get<{ groups: Group[]                   }>('/api/groups?mine=1'),
                api.get<{ communities: Community[]          }>('/api/communities'),
                api.get<{ tags: Tag[]                       }>('/api/tags?category=location'),
                api.get<{ pinned_communities: PinnedCommunity[] }>('/api/me/pinned-communities').catch(() => ({ pinned_communities: [] })),
            ]);
            setMe(m);
            setGroups(g.groups ?? []);
            setCommunities(c.communities ?? []);
            setLocationTags((t.tags ?? []) as Tag[]);
            setPinned(p.pinned_communities ?? []);
        } catch (e) { console.error('manage load failed', e); }
    }, [api, authed]);

    useEffect(() => { load(); }, [load]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to manage your groups &amp; communities.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );

    return (
        <main>
            <div className="manage-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>Manage</h1>
                    <div className="issue">Your groups &amp; communities</div>
                    <div className="rule-bottom" />
                </div>

                <section className="manage-block">
                    <div className="manage-head">
                        <h2>Your groups</h2>
                        <button type="button" className="plain" onClick={() => setShowGroupForm(v => !v)}>+ New group</button>
                    </div>
                    <p className="hint">A group is a business, an interest club, or a civic org. You (as founder) are admin — others can join and eventually publish.</p>
                    <GroupList groups={groups} myUserID={me?.id ?? ''} onChanged={load} />
                    {showGroupForm && (
                        <NewGroupForm
                            onCancel={() => setShowGroupForm(false)}
                            onCreated={async () => { setShowGroupForm(false); await load(); }}
                        />
                    )}
                </section>

                <section className="manage-block">
                    <div className="manage-head">
                        <h2>Your communities</h2>
                        <button type="button" className="plain" onClick={() => setShowCommunityForm(v => !v)}>+ New community</button>
                    </div>
                    <p className="hint">A community is a place-scoped noticeboard (like a suburb). Every community requires at least one location tag.</p>
                    <CommunityList
                        communities={communities}
                        myUserID={me?.id ?? ''}
                        pinnedIDs={new Set(pinned.map(p => p.community_id))}
                        onPinToggle={load}
                    />
                    {showCommunityForm && (
                        <NewCommunityForm
                            locationTags={locationTags}
                            onCancel={() => setShowCommunityForm(false)}
                            onCreated={async () => { setShowCommunityForm(false); await load(); }}
                        />
                    )}
                </section>
            </div>
        </main>
    );
}

function GroupList({ groups, myUserID, onChanged }: { groups: Group[]; myUserID: string; onChanged: () => void }) {
    if (groups.length === 0) {
        return <p className="empty">You haven't created or joined any groups yet. Create one to get started.</p>;
    }
    return (
        <>{groups.map(g => (
            <GroupTile key={g.id} group={g} myUserID={myUserID} onChanged={onChanged} />
        ))}</>
    );
}

interface CommunityListProps {
    communities: Community[];
    myUserID:    string;
    pinnedIDs:   Set<string>;
    onPinToggle: () => void;
}
function CommunityList({ communities, myUserID, pinnedIDs, onPinToggle }: CommunityListProps) {
    const api = useApi();
    if (communities.length === 0) {
        return <p className="empty">No communities yet.</p>;
    }
    async function togglePin(c: Community) {
        if (pinnedIDs.has(c.id)) {
            await api.del('/api/me/pinned-communities/' + c.id).catch(() => {});
        } else {
            await api.post('/api/me/pinned-communities/' + c.id).catch(() => {});
        }
        onPinToggle();
    }
    return (
        <>{communities.map(c => {
            const isMine   = c.created_by_user_id === myUserID;
            const isPinned = pinnedIDs.has(c.id);
            return (
                <div className="tile" key={c.id}>
                    <div className="tile-head">
                        <div className="tile-title">
                            <strong>{c.name}</strong>
                            {isMine   && <span className="tile-role role-admin">founder</span>}
                            {isPinned && <span className="tile-role role-publisher">📌 pinned</span>}
                        </div>
                    </div>
                    {c.tagline && <p className="tile-desc">{c.tagline}</p>}
                    <div className="tile-actions">
                        <span className="tile-slug">/communities/{c.slug}</span>
                        <button type="button" className="link" onClick={() => togglePin(c)}>
                            {isPinned ? 'Unpin' : '📌 Pin to Paper'}
                        </button>
                        {isMine && (
                            <>
                                <Link className="link" to={'/communities/' + c.slug + '/newsroom'}>
                                    🗞 Newsroom →
                                </Link>
                                <Link className="link" to={'/communities/' + c.slug + '/settings'}>
                                    ⚙ Settings →
                                </Link>
                                <Link className="link" to={'/communities/' + c.slug + '/dashboard'}>
                                    📊 Dashboard →
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            );
        })}</>
    );
}

interface FormProps { onCancel: () => void; onCreated: () => Promise<void> | void }

function NewGroupForm({ onCancel, onCreated }: FormProps) {
    const api = useApi();
    const [name,        setName]        = useState('');
    const [slug,        setSlug]        = useState('');
    const [type,        setType]        = useState<GroupType>('business');
    const [description, setDescription] = useState('');
    const [status,      setStatus]      = useState<{ tone: 'ok' | 'err' | 'busy'; msg: string } | null>(null);

    async function onSubmit(e: FormEvent) {
        e.preventDefault();
        setStatus({ tone: 'busy', msg: 'Creating…' });
        try {
            await api.post('/api/groups', { name, slug, type, description: description || null, tag_ids: [] });
            setStatus({ tone: 'ok', msg: '✓ Group created' });
            setTimeout(() => onCreated(), 900);
        } catch (err) {
            setStatus({ tone: 'err', msg: (err as Error).message });
        }
    }
    return (
        <form className="new-form" onSubmit={onSubmit}>
            <h3>Create a group</h3>
            <label>
                <span>Name</span>
                <input type="text" required placeholder="Vredehoek Book Club"
                       value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
                <span>URL slug</span>
                <input type="text" required pattern="[a-z0-9\-]+" placeholder="vredehoek-book-club"
                       value={slug} onChange={(e) => setSlug(e.target.value)} />
                <em>lowercase, dashes, numbers only</em>
            </label>
            <label>
                <span>Kind</span>
                <select value={type} onChange={(e) => setType(e.target.value as GroupType)}>
                    <option value="business">Business (shop, restaurant, café — storefront)</option>
                    <option value="service">Service (handyman, plumber, tutor, gardener — trade)</option>
                    <option value="interest">Interest (club, meetup, hobby)</option>
                    <option value="civic">Civic (NGO, ratepayers, cause)</option>
                </select>
            </label>
            <label>
                <span>Description</span>
                <textarea rows={3} placeholder="What are you about? Who is it for?"
                          value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <div className="actions">
                <button type="button" className="plain" onClick={onCancel}>Cancel</button>
                <button type="submit" className="primary">Create group</button>
                {status && <span className={status.tone === 'err' ? 'err' : status.tone === 'ok' ? 'ok' : ''}>{status.msg}</span>}
            </div>
        </form>
    );
}

function NewCommunityForm({ locationTags, onCancel, onCreated }: FormProps & { locationTags: Tag[] }) {
    const api = useApi();
    const [name,          setName]          = useState('');
    const [slug,          setSlug]          = useState('');
    const [tagline,       setTagline]       = useState('');
    const [description,   setDescription]   = useState('');
    const [locationTagID, setLocationTagID] = useState(locationTags[0]?.id ?? '');
    const [status,        setStatus]        = useState<{ tone: 'ok' | 'err' | 'busy'; msg: string } | null>(null);

    // Keep locationTagID in sync when tags load
    useEffect(() => {
        if (!locationTagID && locationTags.length > 0) setLocationTagID(locationTags[0].id);
    }, [locationTags, locationTagID]);

    async function onSubmit(e: FormEvent) {
        e.preventDefault();
        setStatus({ tone: 'busy', msg: 'Creating…' });
        try {
            await api.post('/api/communities', {
                name, slug,
                tagline:     tagline || null,
                description: description || null,
                location_tag_ids: [locationTagID],
                interest_tag_ids: [],
                posting_policy: 'moderator_approved',
            });
            setStatus({ tone: 'ok', msg: '✓ Community created' });
            setTimeout(() => onCreated(), 900);
        } catch (err) {
            setStatus({ tone: 'err', msg: (err as Error).message });
        }
    }
    return (
        <form className="new-form" onSubmit={onSubmit}>
            <h3>Create a community</h3>
            <label>
                <span>Name</span>
                <input type="text" required placeholder="Vredehoek"
                       value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
                <span>URL slug</span>
                <input type="text" required pattern="[a-z0-9\-]+" placeholder="vredehoek"
                       value={slug} onChange={(e) => setSlug(e.target.value)} />
                <em>lowercase, dashes, numbers only</em>
            </label>
            <label>
                <span>Tagline</span>
                <input type="text" placeholder="Your suburb's noticeboard"
                       value={tagline} onChange={(e) => setTagline(e.target.value)} />
            </label>
            <label>
                <span>Location</span>
                <LocationAutocomplete
                    value={locationTagID}
                    onChange={(id) => setLocationTagID(id)}
                    placeholder="Type your suburb (Vredehoek, Newlands, Sea Point, …)"
                />
                <em>Pick the suburb / neighbourhood this community covers.</em>
            </label>
            <label>
                <span>Description</span>
                <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <div className="actions">
                <button type="button" className="plain" onClick={onCancel}>Cancel</button>
                <button type="submit" className="primary">Create community</button>
                {status && <span className={status.tone === 'err' ? 'err' : status.tone === 'ok' ? 'ok' : ''}>{status.msg}</span>}
            </div>
        </form>
    );
}
